import axios from 'axios';
import { wrapAxiosInstance } from '../cache/modules/axiosFactory';
import { setupAuthInterceptor, retryWithBackoff } from '../common';
import { clearScope } from '../cache/cachePublicMethods';
import { NAMESPACES } from '../configuration';

const BASE_URL = NAMESPACES.menu_api.selection_groups;

// Función auxiliar para configurar la instancia de Axios
const api = setupAuthInterceptor(axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
}));

// Cached API wrapper for this namespace/group
const cachedApi = wrapAxiosInstance(api, {
  namespace: 'menu_api',
  group: 'selection_groups',
  defaultTtl: '5m',
  hashLargeSegments: true,
  emitMeta: true,
});

/**
 * Obtiene todos los grupos de selección por ID local.
 * @param local_id - El ID local asociado al grupo de selección.
 * @returns Una promesa con la respuesta de la API.
 */
export const GetAllSelectionGroupsByLocalId = (local_id: number) => {
  return retryWithBackoff(() =>
    cachedApi.get(`/GetAllSelectionGroupsByLocalId/${local_id}`)
  );
};

/**
 * Crea un nuevo grupo de selección.
 * @param data - Objeto que contiene los datos necesarios para crear el grupo de selección.
 * @returns Una promesa con la respuesta de la API.
 */
export const CreateSelectionGroup = async (data: {
    selection_group_name: string;
    selection_group_description?: string;
    separate_printing?: boolean;
    local_id: number;
  }) => {
    const res = await retryWithBackoff(() =>
      api.post('/CreateSelectionGroup', data)
    );
    await cachedApi.clear();
    return res;
  };

// /DeleteSelectionGroup/<int:selection_group_id>

/**
 * Elimina un grupo de selección.
 * @param selection_group_id - El ID del grupo de selección a eliminar.
 * @returns Una promesa con la respuesta de la API.
 */
export const DeleteSelectionGroup = async (selection_group_id: number) => {
  const res = await retryWithBackoff(() =>
    api.delete(`/DeleteSelectionGroup/${selection_group_id}`)
  );
  await cachedApi.clear();
  return res;
}

/**
 * Obtiene los elementos de un grupo de selección por su ID.
 * @param selection_group_id - El ID del grupo de selección.
 * @returns Una promesa con la respuesta de la API.
 */
export const GetSelectionGroupItemsBySelectionGroupId = (selection_group_id: number) => {
  return retryWithBackoff(() =>
    cachedApi.get(`/GetSelectionGroupItemsBySelectionGroupId/${selection_group_id}`)
  );
};

// /GetAllItemsAssociatedWithSelectionGroup/<int:selection_group_id>

/**
 * Obtiene todos los elementos asociados con un grupo de selección.
 * @param selection_group_id - El ID del grupo de selección.
 * @returns Una promesa con la respuesta de la API.
 */
export const GetAllItemsAssociatedWithSelectionGroup = (selection_group_id: number) => {
  return retryWithBackoff(() =>
    cachedApi.get(`/GetAllItemsAssociatedWithSelectionGroup/${selection_group_id}`)
  );
}

// /AddSelectionGroupToItem 

/**
 * Agrega un grupo de selección a un elemento.
 * @param data - Objeto que contiene los datos necesarios para agregar el grupo de selección al elemento.
 * @returns Una promesa con la respuesta de la API.
 */
export const AddSelectionGroupToItem = async (data: {
    item_id: number;
    selection_group_id: number;
    min_variance?: number;
    max_variance?: number;
    min_selections?: number;
    max_selections?: number;
    local_id?: number;
  }) => {
    const res = await retryWithBackoff(() =>
      api.post('/AddSelectionGroupToItem', data)
    );
    await cachedApi.clear();
    return res;
  };

// /RemoveselectionGroupFromItem/<int:item_id>/<int:selection_group_id>

/**
 * Elimina un grupo de selección de un elemento.
 * @param item_id - El ID del elemento.
 * @param selection_group_id - El ID del grupo de selección.
 * @returns Una promesa con la respuesta de la API.
 */

export const RemoveselectionGroupFromItem = async (item_id: number, selection_group_id: number) => {
  const res = await retryWithBackoff(() =>
    api.delete(`/RemoveselectionGroupFromItem/${item_id}/${selection_group_id}`)
  );
  await cachedApi.clear();
  return res;
}

// /UpdateItemSelectionGroupMinVariance/<int:item_id>/<int:selection_group_id> 

/**
 * Actualiza la variación mínima de un grupo de selección en un elemento.
 * @param item_id - El ID del elemento.
 * @param selection_group_id - El ID del grupo de selección.
 * @param min_variance - La nueva variación mínima.
 * @returns Una promesa con la respuesta de la API.
 */
export const UpdateItemSelectionGroupMinVariance = async (
  item_id: number,
  selection_group_id: number,
  min_variance: number
) => {
    const res = await retryWithBackoff(() =>
      api.put(`/UpdateItemSelectionGroupMinVariance/${item_id}/${selection_group_id}`, { min_variance })
    );
    await cachedApi.clear();
    return res;
    }

// /UpdateItemSelectionGroupMaxVariance/<int:item_id>/<int:selection_group_id>

/**
 * Actualiza la variación máxima de un grupo de selección en un elemento.
 * @param item_id - El ID del elemento.
 * @param selection_group_id - El ID del grupo de selección.
 * @param max_variance - La nueva variación máxima.
 * @returns Una promesa con la respuesta de la API.
 */
export const UpdateItemSelectionGroupMaxVariance = async (
  item_id: number,
  selection_group_id: number,
  max_variance: number
) => {
    const res = await retryWithBackoff(() =>
      api.put(`/UpdateItemSelectionGroupMaxVariance/${item_id}/${selection_group_id}`, { max_variance })
    );
    await cachedApi.clear();
    return res;
    }

// /UpdateItemSelectionGroupMinSelections/<int:item_id>/<int:selection_group_id>

/**
 * Actualiza el número mínimo de selecciones de un grupo de selección en un elemento.
 * @param item_id - El ID del elemento.
 * @param selection_group_id - El ID del grupo de selección.
 * @param min_selections - El nuevo número mínimo de selecciones.
 * @returns Una promesa con la respuesta de la API.
 */
export const UpdateItemSelectionGroupMinSelections = async (
  item_id: number,
  selection_group_id: number,
  min_selections: number
) => {
    const res = await retryWithBackoff(() =>
      api.put(`/UpdateItemSelectionGroupMinSelections/${item_id}/${selection_group_id}`, { min_selections })
    );
    await cachedApi.clear();
    return res;
    }

// /UpdateItemSelectionGroupMaxSelections/<int:item_id>/<int:selection_group_id>

/**
 * Actualiza el número máximo de selecciones de un grupo de selección en un elemento.
 * @param item_id - El ID del elemento.
 * @param selection_group_id - El ID del grupo de selección.
 * @param max_selections - El nuevo número máximo de selecciones.
 * @returns Una promesa con la respuesta de la API.
 */
export const UpdateItemSelectionGroupMaxSelections = async (
  item_id: number,
  selection_group_id: number,
  max_selections: number
) => {
    const res = await retryWithBackoff(() =>
      api.put(`/UpdateItemSelectionGroupMaxSelections/${item_id}/${selection_group_id}`, { max_selections })
    );
    await cachedApi.clear();
    return res;
    }

/**
 * Actualiza el valor mínimo de un elemento en un grupo de selección.
 * @param selection_group_id - El ID del grupo de selección.
 * @param item_id - El ID del elemento.
 * @param min_value - El nuevo valor mínimo.
 * @returns Una promesa con la respuesta de la API.
 */
export const UpdateSelectionGroupItemMinValue = async (
  selection_group_id: number,
  item_id: number,
  min_value: number
) => {
  const res = await retryWithBackoff(() =>
    api.put(`/UpdateSelectionGroupItemMinValue/${selection_group_id}/${item_id}/${min_value}`)
  );
  await cachedApi.clear();
  return res;
};

/**
 * Actualiza el valor máximo de un elemento en un grupo de selección.
 * @param selection_group_id - El ID del grupo de selección.
 * @param item_id - El ID del elemento.
 * @param max_value - El nuevo valor máximo.
 * @returns Una promesa con la respuesta de la API.
 */
export const UpdateSelectionGroupItemMaxValue = async (
  selection_group_id: number,
  item_id: number,
  max_value: number
) => {
  const res = await retryWithBackoff(() =>
    api.put(`/UpdateSelectionGroupItemMaxValue/${selection_group_id}/${item_id}/${max_value}`)
  );
  await cachedApi.clear();
  return res;
};

/**
 * Actualiza el modificador de precio de un elemento en un grupo de selección.
 * @param selection_group_id - El ID del grupo de selección.
 * @param item_id - El ID del elemento.
 * @param price_type - El tipo de precio a modificar ('desk', 'delivery', 'online', 'restaurant').
 * @param price_value - El nuevo valor del modificador de precio.
 * @returns Una promesa con la respuesta de la API.
 */
export const UpdateSelectionGroupItemPriceModifier = async (
  selection_group_id: number,
  item_id: number,
  price_type: string,
  price_value: number
) => {
  const res = await retryWithBackoff(() =>
    api.put(
      `/UpdateSelectionGroupItemPriceModifier/${selection_group_id}/${item_id}`,
      { price_type, price_value }
    )
  );
  await cachedApi.clear();
  return res;
};

/**
 * Agrega un ítem a un grupo de selección.
 * @param data - Objeto que contiene los datos necesarios para agregar el ítem al grupo de selección.
 * @returns Una promesa con la respuesta de la API.
 */
export const AddItemToSelectionGroup = async (data: {
    selection_group_id: number;
    item_id: number;
    desk_price_modifier?: number;
    delivery_price_modifier?: number;
    online_price_modifier?: number;
    price_modifier?: number;
    min_selections?: number;
    max_selections?: number;
  }) => {
    const res = await retryWithBackoff(() =>
      api.post('/AddItemToSelectionGroup', data)
    );
    await cachedApi.clear();
    return res;
  };

  /**
 * Actualiza el nombre a un grupo de selección.
 * @param data - Objeto que contiene los datos necesarios para agregar el ítem al grupo de selección.
 * @returns Una promesa con la respuesta de la API.
 */
export const UpdateselectionGroupName = async (data: {
    selection_group_name: string;
  },selection_group_id:number) => {
    const res = await retryWithBackoff(() =>
      api.put(`/UpdateSelectionGroupName/${selection_group_id}`, data)
    );
    await cachedApi.clear();
    return res;
  };

    /**
 * Actualiza el nombre a un grupo de selección.
 * @param data - Objeto que contiene los datos necesarios para agregar el ítem al grupo de selección.
 * @returns Una promesa con la respuesta de la API.
 */
export const UpdateselectionGroupDescription = async (data: {
    selection_group_description: string;
  },selection_group_id:number) => {
    const res = await retryWithBackoff(() =>
      api.put(`/UpdateSelectionGroupDescription/${selection_group_id}`, data)
    );
    await cachedApi.clear();
    return res;
  };

// /RemoveItemFromSelectionGroup/<int:selection_group_id>/<int:item_id>

/**
 * Elimina un ítem de un grupo de selección.
 * @param selection_group_id - El ID del grupo de selección.
 * @param item_id - El ID del ítem a eliminar.
 * @returns Una promesa con la respuesta de la API.
 */

export const RemoveItemFromSelectionGroup = async (selection_group_id: number, item_id: number) => {
  const res = await retryWithBackoff(() =>
    api.delete(`/RemoveItemFromSelectionGroup/${selection_group_id}/${item_id}`)
  );
  await cachedApi.clear();
  return res;
}

export const deleteSelectedSelectionGroups = async (selection_group_ids: number[]) => {
  const res = await retryWithBackoff(() =>
    api.delete('/DeleteSelectedSelectionGroups',{ data: { selection_group_ids}})
  );
  await cachedApi.clear();
  return res;
};

/**
 * Get selection groups configured for a specific item with detailed structure.
 * @param item_id - The item identifier.
 * @returns A promise with the API response.
 */
export const GetSelectionGroupsForItemById = (item_id: number) => {
  return retryWithBackoff(() =>
    cachedApi.get(`/GetSelectionGroupsForItemById/${item_id}`)
  );
};

/**
 * Toggle the separate_printing flag of a selection group (true to false or false to true).
 * @param selection_group_id - The ID of the selection group.
 * @returns A promise with the API response.
 */
export const ToggleSeparatePrintingBySelectionGroupId = async (selection_group_id: number) => {
  const res = await retryWithBackoff(() =>
    api.put(`/ToggleSeparatePrintingBySelectionGroupId/${selection_group_id}`)
  );
  await cachedApi.clear();
  return res;
};

// ==========================================
// Third-Party Provider Specific Pricing for Selection Group Items
// ==========================================

// Get all provider-specific price modifiers for a selection group item
export const getSelectionGroupItemThirdPartyPrices = async (selection_group_id: number, item_id: number) => {
  return await retryWithBackoff(() =>
    api.get(`/GetSelectionGroupItemThirdPartyPrices/${selection_group_id}/${item_id}`)
  );
};

// Set a provider-specific price modifier for a selection group item
export const setSelectionGroupItemThirdPartyPrice = async (
  selection_group_id: number, 
  item_id: number, 
  third_party_provider_id: number, 
  price_modifier: number
) => {
  const res = await retryWithBackoff(() =>
    api.put(`/SetSelectionGroupItemThirdPartyPrice/${selection_group_id}/${item_id}/${third_party_provider_id}`, { price_modifier })
  );
  await clearScope({ namespace: 'menu_api', group: 'selection_groups' });
  await clearScope({ namespace: 'menu_api', group: 'menus' });
  return res;
};

// Delete a provider-specific price modifier (falls back to delivery_price_modifier)
export const deleteSelectionGroupItemThirdPartyPrice = async (
  selection_group_id: number, 
  item_id: number, 
  third_party_provider_id: number
) => {
  const res = await retryWithBackoff(() =>
    api.delete(`/DeleteSelectionGroupItemThirdPartyPrice/${selection_group_id}/${item_id}/${third_party_provider_id}`)
  );
  await clearScope({ namespace: 'menu_api', group: 'selection_groups' });
  await clearScope({ namespace: 'menu_api', group: 'menus' });
  return res;
};